import {createRoute, type AnyRoute} from '@tanstack/react-router'
import type {RouteObject as ReactRouterRouteObject} from '../react-router'
import {routeOptionsAdapter} from './route-options-adapter'
import type {GetRouteOptions} from './types'

/**
 * Recursively adapts a React Router `RouteObject` tree into TanStack Router route instances.
 *
 * Each top-level route is created beneath `parentRoute`, and nested `children` retain their
 * hierarchy beneath the corresponding adapted route. Paths are normalized relative to
 * `parentFullPath` before TanStack initializes the tree, and `getRouteOptions` can augment the
 * generated options for every route.
 *
 * Each top-level route's `getParentRoute` resolves to the provided `parentRoute`, establishing the
 * ancestry TanStack uses during initialization. However, the adapter defers updating
 * `parentRoute.children` to the caller, which decides whether to merge the returned routes with
 * existing children or replace them. By contrast, nested routes are attached here because they
 * belong entirely to the adapted subtree.
 */
export function adaptRouteTreeToTanStack(
  routes: ReactRouterRouteObject[],
  parentRoute: AnyRoute,
  options: {
    parentFullPath: string
    getRouteOptions?: GetRouteOptions
  } = {parentFullPath: ''},
): AnyRoute[] {
  const {parentFullPath, getRouteOptions} = options
  return routes.map(routeObj => {
    const {normalizedRelativePath, routeIdentifier, ...routeOptions} = routeOptionsAdapter(
      routeObj,
      parentFullPath,
      getRouteOptions,
    )

    const route = createRoute({
      getParentRoute: () => parentRoute,
      ...('path' in routeIdentifier ? {path: routeIdentifier.path} : {id: routeIdentifier.id}),
      ...routeOptions,
    })

    if (routeObj.children && routeObj.children.length > 0) {
      // Use normalizedRelativePath to build the new parent path because route.fullPath
      // won't be available until the route is added to the tree
      const newParentPath = `${parentFullPath}/${normalizedRelativePath ?? ''}`.replace(/\/+/g, '/')
      const childRoutes = adaptRouteTreeToTanStack(routeObj.children, route, {
        parentFullPath: newParentPath,
        getRouteOptions,
      })
      route.addChildren(childRoutes)
    }

    return route
  })
}
